import { EMPTY_STRING } from '@modeso/aumico-lib-settings-fe';
import {
    IAccountMappingTemplate,
    IEquityMovementEntry,
    IEquityMovementSchedule,
    ITranslationEntry,
} from '@modeso/types__aumico-lib-annualreport-be';
import { Translation } from '@modeso/aumico-lib-core-fe';
import { ItemType } from '@modeso/types__aumico-lib-templates-be';
import { I18NextService } from 'angular-i18next';
import { EquityMovementScheduleHelper } from '../../helpers/equity-movement-schedule.helper';
import { GapReportingVisitor } from './GapReportingVisitor';
import { ITranslatorVisitor } from './ITranslatorVisitor.interface';
import { UntranslatedTexts } from './UntranslatedTexts';
import { Ems } from './classes/Ems';

const EMS_ROW_TITLE_KEY = 'aumico_annual_report_ems_row_title';
const EMS_TOTAL_EQUITY_KEY = 'aumico_annual_report_ems_total_equity_header_text';

export class EmsTranslatorVisitor extends GapReportingVisitor implements ITranslatorVisitor {
    constructor(
        private readonly reportLanguage: string,
        private readonly targetLanguage: string,
        private readonly i18Next: I18NextService,
        private readonly balanceTemplate: IAccountMappingTemplate,
        private readonly translatableItems: Map<string, Translation>,
        untranslated?: UntranslatedTexts,
        translations?: Record<string, ITranslationEntry>,
    ) {
        super(untranslated, 'ems', translations);
    }

    visit(ems: Ems): void {
        const { equityMovementSchedule: schedule } = ems;

        schedule.textAdditionAboveEms = this.resolve(schedule.textAdditionAboveEms);
        schedule.textAdditionBelowEms = this.resolve(schedule.textAdditionBelowEms);

        this.resolvePeriodTitleIfEdited(schedule, 'previousOpeningTitle');
        this.resolvePeriodTitleIfEdited(schedule, 'previousClosingTitle');
        this.resolvePeriodTitleIfEdited(schedule, 'currentOpeningTitle');
        this.resolvePeriodTitleIfEdited(schedule, 'currentClosingTitle');

        const captionsByFliName = this.equityFliCaptions();

        schedule.equityMovementEntries.forEach((entry) => {
            // Same order the print resolves it in: a custom name wins over the balance-tree caption.
            const columnHead = entry.customColumnName ?? captionsByFliName.get(entry.fliName);
            // Writing an empty string here would rob the print of its `null`, and with it the i18n
            // header it substitutes for the total column.
            if (typeof columnHead === 'string' && columnHead !== EMPTY_STRING) {
                entry.customColumnName = this.resolve(columnHead, this.columnHeadFallback(entry, columnHead));
            }

            entry.currentMovements.forEach((movement) => (movement.name = this.resolve(movement.name)));
            entry.previousMovements.forEach((movement) => (movement.name = this.resolve(movement.name)));
        });
    }

    /**
     * An edited title goes through the block. A still-default one does too, with its translated label
     * as the standard — same as the AOP and cash-flow defaults, so it is editable in the review. Only
     * an unset title stays out: there is no report text, the print builds label + date itself.
     */
    private resolvePeriodTitleIfEdited(
        schedule: IEquityMovementSchedule,
        key: 'previousOpeningTitle' | 'previousClosingTitle' | 'currentOpeningTitle' | 'currentClosingTitle',
    ): void {
        const stored = schedule[key];
        if (!this.isDefaultPeriodTitle(stored)) {
            schedule[key] = this.resolve(stored);

            return;
        }
        if (stored == null || stored === EMPTY_STRING) {
            schedule[key] = this.translatedDefaultPeriodTitle(stored); // unchanged: unset stays unset
            return;
        }
        schedule[key] = this.resolve(stored, this.translatedDefaultPeriodTitle(stored));
    }

    /** `Eigenkapital per 31.12.2025` -> `Capitaux propres au 31.12.2025`. */
    private translatedDefaultPeriodTitle(stored: string | null | undefined): string {
        const targetLabel = this.i18Next.t(EMS_ROW_TITLE_KEY, { lng: this.targetLanguage }) as string;
        if (targetLabel === EMPTY_STRING || targetLabel === EMS_ROW_TITLE_KEY) {
            return stored ?? EMPTY_STRING;
        }
        // An unset title stays unset: the print appends the date to the label itself in that case.
        if (stored == null || stored === EMPTY_STRING) {
            return stored ?? EMPTY_STRING;
        }
        const reportLabel = this.i18Next.t(EMS_ROW_TITLE_KEY, { lng: this.reportLanguage }) as string;

        return targetLabel + stored.slice(reportLabel.length);
    }

    /** Still reads like the master data (or, for a total column, like the i18n header)? Then the
     * maintained translation is the answer and nothing is reported. */
    private columnHeadFallback(entry: IEquityMovementEntry, columnHead: string): string | undefined {
        if (entry.customColType === 'TOTAL') {
            return this.labelIfStillDefault(EMS_TOTAL_EQUITY_KEY, columnHead);
        }

        const translation = this.translatableItems?.get(`${entry.fliName}_${ItemType.FLI}`);
        if (translation == null || translation[this.reportLanguage] !== columnHead) {
            return undefined;
        }
        const caption = translation[this.targetLanguage];

        return typeof caption === 'string' && caption !== EMPTY_STRING ? caption : undefined;
    }

    /** The target-language label, but only while `stored` still is the report-language one. */
    private labelIfStillDefault(key: string, stored: string): string | undefined {
        const reportLabel = this.i18Next.t(key, { lng: this.reportLanguage }) as string;
        if (reportLabel !== stored) {
            return undefined;
        }
        const targetLabel = this.i18Next.t(key, { lng: this.targetLanguage }) as string;

        return targetLabel !== EMPTY_STRING && targetLabel !== key ? targetLabel : undefined;
    }

    /** Through the same helper the print uses, so the reported text is the printed one. */
    private equityFliCaptions(): Map<string, string> {
        const group = this.balanceTemplate?.group;
        const equityGroupName = this.balanceTemplate?.equityGroupName;
        if (group == null || equityGroupName == null) {
            return new Map<string, string>();
        }

        const equityGroupFlis = EquityMovementScheduleHelper.extractFlisFromEquityGroup([group], equityGroupName);

        return new Map<string, string>(equityGroupFlis.map((fli) => [fli.fliName, fli.fliDescriptionToDisplay]));
    }

    /** Only the label decides: the date is formatted output and would not compare reliably. */
    private isDefaultPeriodTitle(stored: string | null | undefined): boolean {
        if (stored == null || stored === EMPTY_STRING) {
            return true;
        }

        const label = this.i18Next.t(EMS_ROW_TITLE_KEY, { lng: this.reportLanguage }) as string;
        if (label === EMPTY_STRING || label === EMS_ROW_TITLE_KEY) {
            return false;
        }

        return stored === label || stored.startsWith(`${label} `);
    }
}
